import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { EchoService } from '../echo.service';
import { DeliverableCardPayload } from './gable.service';

/**
 * Discriminated union of all `ai.stream.*` events the backend emits during a
 * streaming assistant turn. The `type` literals match the Laravel
 * `broadcastAs` names (without the `.` prefix Echo's `.listen()` requires).
 */
export type AiStreamEvent =
    | AiStreamStartedEvent
    | AiStreamTextDeltaEvent
    | AiStreamNarrationEvent
    | AiStreamToolCallEvent
    | AiStreamToolResultEvent
    | AiStreamCompletedEvent
    | AiStreamFailedEvent
    | AiStreamGapEvent
    | AiStreamPendingQuestionsEvent
    | AiStreamThinkingDeltaEvent
    | AiStreamSummaryDeltaEvent
    | AiStreamFollowUpsEvent
    | AiConversationTitledEvent
    | AiSandboxProgressEvent
    | AiSandboxReadyEvent
    | AiSandboxFailedEvent
    | AiStreamChannelErrorEvent;

/**
 * Every `ai.stream.*` event the box publishes carries `seq`: per assistant message,
 * contiguous from 1, the completed / failed event last. The broker is not trusted for
 * order or delivery across publishes (Pusher offered neither; Ably orders per publisher
 * but a reconnect can still drop the tail), so `StreamSequencer` restores order before
 * the drawer sees an event. Events core publishes (titled, sandbox, follow-ups) have no `seq`.
 */
export interface AiStreamSequenced {
    seq?: number;
    seqEpoch?: string;
}

export interface AiStreamStartedEvent extends AiStreamSequenced {
    type: 'ai.stream.started';
    conversationId: number;
    messageId: number;
    startedAt: string;
}

export interface AiStreamTextDeltaEvent extends AiStreamSequenced {
    type: 'ai.stream.text-delta';
    conversationId: number;
    messageId: number;
    delta: string;
}

/** Text the model wrote before a tool call: already streamed as text-delta, now reclassified as a reasoning step. */
export interface AiStreamNarrationEvent extends AiStreamSequenced {
    type: 'ai.stream.narration';
    conversationId: number;
    messageId: number;
    text: string;
}

export interface AiStreamToolCallEvent extends AiStreamSequenced {
    type: 'ai.stream.tool-call';
    conversationId: number;
    messageId: number;
    toolName: string;
    toolCallId: string;
    label: string;
}

export interface AiStreamToolResultEvent extends AiStreamSequenced {
    type: 'ai.stream.tool-result';
    conversationId: number;
    messageId: number;
    toolCallId: string;
    ok: boolean;
}

/**
 * Synthesized client-side, never broadcast: the sequencer waited for a missing
 * `seq` and gave up. `missing` events for this message were lost or are still
 * in flight; the saved message on the server is the only complete copy.
 */
export interface AiStreamGapEvent {
    type: 'ai.stream.gap';
    conversationId: number;
    messageId: number;
    missing: number;
}

export interface AiStreamCompletedEvent extends AiStreamSequenced {
    type: 'ai.stream.completed';
    conversationId: number;
    messageId: number;
    toolsUsed: string[];
    pendingProposals: unknown[];
    /**
     * If the model fired `ask_user_questions` this turn, the completed
     * event mirrors the structured batch alongside / instead of any
     * proposals. The page treats `ai.stream.pending-questions` and this
     * field as the same trigger — whichever arrives first wins.
     */
    pendingQuestions?: GablePendingQuestions | null;
    /**
     * Telemetry event id for this turn — wires thumbs-up / down feedback
     * on the assistant bubble. Null / absent if telemetry logging failed.
     */
    aiInteractionEventId?: number | null;
    /**
     * AI-suggested follow-up prompts for this turn. Empty / absent means
     * the orchestrator didn't surface any; UI falls back to demoSuggestions.
     */
    followUpSuggestions?: string[];
    /**
     * Structured tool-result cards the UI renders inline (currently only
     * `generate_report` produces these). The page attaches them to the
     * assistant bubble for this messageId and renders below the text.
     */
    toolCards?: { tool: string; payload: Record<string, unknown> }[];
}

export interface AiStreamFailedEvent extends AiStreamSequenced {
    type: 'ai.stream.failed';
    conversationId: number;
    messageId: number;
    error: string;
}

/**
 * Synthesized client-side (never broadcast): the Echo channel itself errored
 * (auth 403, socket drop) mid-turn. The box is unaffected and may still finish
 * the turn server-side — this must NOT be treated as the turn failing. The
 * consumer keeps `isSending` true and leans on its poll-against-core fallback
 * for a terminal streamState, bounded by the stream watchdog.
 */
export interface AiStreamChannelErrorEvent {
    type: 'ai.stream.channel-error';
    conversationId: number;
}

/**
 * The model fired `ask_user_questions` mid-turn. The streamed assistant
 * bubble's text is final at this point — the backend marks it complete
 * and broadcasts this event back-to-back with `ai.stream.completed`.
 *
 * `messageId` is the SAME id as the in-flight streaming bubble — render
 * the card alongside / below that bubble; do NOT create a new message
 * row. The page should also stop accepting text-deltas for this id.
 */
export interface AiStreamPendingQuestionsEvent extends AiStreamSequenced {
    type: 'ai.stream.pending-questions';
    conversationId: number;
    messageId: number;
    batchId: string;
    leadIn: string;
    questions: GableQuestion[];
}

/**
 * Question kinds the backend can emit. Future kinds may be added — UI
 * falls back to a `text` input + console warning for unknown values.
 */
export type GableQuestionKind =
    | 'single_select'
    | 'multi_select'
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | GablePickerKind;

/**
 * Entity-picker kinds — the question card opens the V2 selector dialog
 * for that entity type and returns picked IDs as the answer value. Only
 * `unit_picker` is wired today; the rest are stubs that render a "coming
 * soon" placeholder.
 */
export type GablePickerKind =
    | 'unit_picker'
    | 'vendor_picker'
    | 'member_picker'
    | 'owner_picker'
    | 'document_picker'
    | 'request_picker'
    | 'violation_picker'
    | 'charge_picker'
    | 'category_picker'
    | 'account_picker';

export const GABLE_PICKER_KINDS: GablePickerKind[] = [
    'unit_picker',
    'vendor_picker',
    'member_picker',
    'owner_picker',
    'document_picker',
    'request_picker',
    'violation_picker',
    'charge_picker',
    'category_picker',
    'account_picker',
];

export interface GableQuestionOption {
    value: string;
    label: string;
    recommended?: boolean;
    hint?: string;
}

export interface GableQuestion {
    id: string;
    kind: GableQuestionKind | string; // string fallback for forward-compat
    prompt: string;
    context?: string;
    help?: string;
    required: boolean;
    allow_other?: boolean;
    default?: string | string[];
    options?: GableQuestionOption[];
}

export interface GablePendingQuestions {
    batchId: string;
    leadIn: string;
    questions: GableQuestion[];
}

/**
 * Streamed extended-thinking content from the model. Emitted as deltas
 * before / interleaved with `text-delta` events when the backend has
 * Anthropic extended-thinking enabled. The UI accumulates these into a
 * separate "reasoning" block above the assistant prose bubble — they
 * are NOT user-facing answer content and must not be appended to the
 * message bubble itself.
 *
 * Backend contract: emit one event per Claude `thinking` content block
 * delta. Concatenating all `delta`s for a given `messageId` yields the
 * full thinking text for that turn.
 */
export interface AiStreamThinkingDeltaEvent extends AiStreamSequenced {
    type: 'ai.stream.thinking-delta';
    conversationId: number;
    messageId: number;
    delta: string;
}

/**
 * Layperson-safe paraphrase of one phase of the model's reasoning.
 * Emitted once per phase by the backend after a Haiku summarizer +
 * denylist pass over the raw thinking. The backend NEVER broadcasts
 * raw thinking now — only these. `phaseToolCallId` keys the summary
 * to the tool row it belongs above (or null for end-of-turn summaries).
 */
export interface AiStreamSummaryDeltaEvent extends AiStreamSequenced {
    type: 'ai.stream.summary-delta';
    conversationId: number;
    messageId: number;
    summary: string;
    phaseToolCallId: string | null;
}

/**
 * Per-result follow-up suggestion chips. Emitted after AiStreamCompleted
 * because the chips come from a separate Haiku call — letting it block
 * completion would add ~1s of "still thinking" latency for every answer.
 * The UI swaps the (currently empty) chip list from the completed event
 * for these specific-to-this-result suggestions.
 */
export interface AiStreamFollowUpsEvent {
    type: 'ai.stream.follow-ups';
    conversationId: number;
    messageId: number;
    suggestions: string[];
    /**
     * Gable-box turns deliver the telemetry id here (their completed event
     * predates it) so thumbs can render. Absent/null on legacy turns.
     */
    aiInteractionEventId?: number | null;
}

/**
 * Conversation title generated (SD-6742). Fired by a post-completion Haiku job
 * on the first turn of a brand-new conversation, on the SAME channel. Lets the
 * drawer swap the header title + recent-chats entry live.
 */
export interface AiConversationTitledEvent {
    type: 'ai.conversation.titled';
    conversationId: number;
    title: string;
}

/**
 * Sandbox deliverable events (SD-7240). When the model calls `build_document`
 * for anything other than `board_summary`, generation routes to a code-exec
 * sandbox that finishes ASYNCHRONOUSLY — minutes after `ai.stream.completed`.
 * These three events arrive on the SAME `private-ai-conversation-{id}` channel
 * the stream uses, so the consumer must keep that subscription open past
 * turn-completion until `ready` / `failed` lands.
 *
 * `progress` fires several times as a live milestone timeline — use `label`
 * as the user-facing text. `ready` carries the finished deliverable card in
 * exactly the shape `GableDeliverableCardComponent` renders. `failed` clears
 * the pending state and surfaces `message`.
 */
export interface AiSandboxProgressEvent {
    type: 'ai.sandbox.progress';
    conversationId: number;
    // reserved for SD-7242 (per-phase timeline); intentionally unused for now
    phase: string;
    label: string;
    /** Identifies one generation run — multiple may be in flight per conversation. May be '' / absent on older events. */
    runId?: string;
}

export interface AiSandboxReadyEvent {
    type: 'ai.sandbox.ready';
    conversationId: number;
    card: DeliverableCardPayload & { deliverable_artifact_id?: number };
    /** Identifies one generation run — multiple may be in flight per conversation. May be '' / absent on older events. */
    runId?: string;
}

export interface AiSandboxFailedEvent {
    type: 'ai.sandbox.failed';
    conversationId: number;
    message: string;
    /** Identifies one generation run — multiple may be in flight per conversation. May be '' / absent on older events. */
    runId?: string;
}

/**
 * Handle returned from `subscribe()`. Owns the Echo channel binding and the
 * multiplexed event subject — pages should call `unsubscribe()` from
 * `ngOnDestroy` (or once a turn completes/fails) so we leave the channel and
 * complete the subject promptly.
 */
export interface AiConversationStreamHandle {
    events$: Observable<AiStreamEvent>;
    close: () => void;
}

/** How long a missing `seq` may stay missing before the held events are released around it. */
export const STREAM_GAP_WAIT_MS = 1500;
const FINISHED_MESSAGES_REMEMBERED = 50;

interface SequencedMessageState {
    expected: number;
    held: Map<number, AiStreamEvent>;
    gapTimer: ReturnType<typeof setTimeout> | null;
    epoch?: string;
}

/**
 * Puts the box's events back in `seq` order per assistant message. In-order
 * events pass straight through; an early one waits (up to STREAM_GAP_WAIT_MS)
 * for the ones before it, then the gap is announced with `ai.stream.gap` and
 * everything held is released in order. Duplicates and stragglers that land
 * after the message finished are dropped. Events without `seq` pass through.
 */
export class StreamSequencer {
    private readonly _messages = new Map<number, SequencedMessageState>();
    private readonly _finished: number[] = [];

    constructor(private readonly _emit: (event: AiStreamEvent) => void) {}

    push(event: AiStreamEvent): void {
        const seq = (event as AiStreamSequenced).seq;
        const messageId = (event as { messageId?: unknown }).messageId;
        if (typeof seq !== 'number' || typeof messageId !== 'number') {
            this._emit(event);
            return;
        }
        if (this._finished.includes(messageId)) {
            return;
        }
        // The first event seen sets the baseline: a drawer that re-joins a turn already in
        // progress (reload, second tab) has legitimately missed everything before it.
        const state = this._stateFor(messageId, seq);
        // A turn picked up by another box task restarts its numbering, which used to look
        // like a flood of stale events and stalled the drawer. A new epoch is the box
        // saying so, and the baseline moves with it (SD-10033).
        const epoch = (event as AiStreamSequenced).seqEpoch;
        if (typeof epoch === 'string' && state.epoch !== undefined && state.epoch !== epoch) {
            if (state.gapTimer !== null) {
                clearTimeout(state.gapTimer);
            }
            state.held.clear();
            state.gapTimer = null;
            state.expected = seq;
        }
        if (typeof epoch === 'string') {
            state.epoch = epoch;
        }
        if (seq < state.expected || state.held.has(seq)) {
            return;
        }
        if (seq > state.expected) {
            state.held.set(seq, event);
            if (state.gapTimer === null) {
                state.gapTimer = setTimeout(() => this._releaseAroundGap(messageId), STREAM_GAP_WAIT_MS);
            }
            return;
        }
        this._deliver(messageId, state, event);
        this._drain(messageId, state);
    }

    dispose(): void {
        this._messages.forEach((state) => {
            if (state.gapTimer !== null) {
                clearTimeout(state.gapTimer);
            }
        });
        this._messages.clear();
    }

    private _stateFor(messageId: number, firstSeq: number): SequencedMessageState {
        let state = this._messages.get(messageId);
        if (state === undefined) {
            state = { expected: firstSeq, held: new Map(), gapTimer: null, epoch: undefined };
            this._messages.set(messageId, state);
        }
        return state;
    }

    private _drain(messageId: number, state: SequencedMessageState): void {
        let next = state.held.get(state.expected);
        while (next !== undefined) {
            state.held.delete(state.expected);
            this._deliver(messageId, state, next);
            next = state.held.get(state.expected);
        }
        if (state.held.size === 0 && state.gapTimer !== null) {
            clearTimeout(state.gapTimer);
            state.gapTimer = null;
        }
    }

    private _releaseAroundGap(messageId: number): void {
        const state = this._messages.get(messageId);
        if (state === undefined) {
            return;
        }
        state.gapTimer = null;
        const heldSeqs = Array.from(state.held.keys()).sort((a, b) => a - b);
        if (heldSeqs.length === 0) {
            return;
        }
        let missing = 0;
        let cursor = state.expected;
        for (const seq of heldSeqs) {
            missing += seq - cursor;
            cursor = seq + 1;
        }
        const first = state.held.get(heldSeqs[0]) as AiStreamEvent;
        this._emit({
            type: 'ai.stream.gap',
            conversationId: (first as { conversationId: number }).conversationId,
            messageId,
            missing,
        });
        for (const seq of heldSeqs) {
            const event = state.held.get(seq) as AiStreamEvent;
            state.held.delete(seq);
            state.expected = seq;
            this._deliver(messageId, state, event);
        }
    }

    private _deliver(messageId: number, state: SequencedMessageState, event: AiStreamEvent): void {
        state.expected += 1;
        this._emit(event);
        if (event.type === 'ai.stream.completed' || event.type === 'ai.stream.failed') {
            if (state.gapTimer !== null) {
                clearTimeout(state.gapTimer);
            }
            this._messages.delete(messageId);
            this._finished.push(messageId);
            if (this._finished.length > FINISHED_MESSAGES_REMEMBERED) {
                this._finished.shift();
            }
        }
    }
}

/**
 * Subscribes to the per-conversation Echo channel that carries the AI
 * assistant's streamed-thinking events.
 *
 * Channel: `private-ai-conversation-{conversationId}` (Echo prefixes
 * `private-` automatically when `.private()` is used). All-lowercase +
 * hyphens — the project has been bitten by case-sensitive channel names
 * in Echo before, so we standardize on this shape.
 *
 * The page owns the subscription lifecycle — we deliberately do NOT keep a
 * map of channels here. One handle per active conversation; the caller is
 * responsible for calling `unsubscribe()` when the turn finishes, the user
 * navigates away, or a new turn supersedes the old one.
 */
@Injectable({
    providedIn: 'root',
})
export class GableStreamService {
    constructor(private _echo: EchoService) {}

    /**
     * Open a streaming subscription for the given conversation. Returns a
     * handle exposing a hot observable of the six event types and a
     * `close()` function that leaves the Echo channel and completes the
     * subject.
     *
     * Named `open` (not `subscribe`) to keep the meta-lint
     * `subscribe-until-destroyed` heuristic happy — that rule scans for the
     * literal `.subscribe(` token and would otherwise flag callers that pass
     * the result through `untilDestroyed`.
     */
    public open(conversationId: number): AiConversationStreamHandle {
        const channelName = `ai-conversation-${conversationId}`;
        const subject = new Subject<AiStreamEvent>();
        const sequencer = new StreamSequencer((event) => subject.next(event));
        const channel = this._echo.private(channelName);

        // Note: leading dot tells Echo to use the literal Laravel
        // `broadcastAs` name rather than namespacing it under
        // `App\\Events\\...`. This must match the API agent's broadcast
        // class names exactly — see contract in workstream E.

        channel.listen('.ai.stream.started', (data: Omit<AiStreamStartedEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.started', ...data });
        });

        channel.listen('.ai.stream.text-delta', (data: Omit<AiStreamTextDeltaEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.text-delta', ...data });
        });

        channel.listen('.ai.stream.narration', (data: Omit<AiStreamNarrationEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.narration', ...data });
        });

        channel.listen('.ai.stream.tool-call', (data: Omit<AiStreamToolCallEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.tool-call', ...data });
        });

        channel.listen('.ai.stream.tool-result', (data: Omit<AiStreamToolResultEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.tool-result', ...data });
        });

        channel.listen('.ai.stream.completed', (data: Omit<AiStreamCompletedEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.completed', ...data });
        });

        channel.listen('.ai.stream.failed', (data: Omit<AiStreamFailedEvent, 'type'>) => {
            sequencer.push({ type: 'ai.stream.failed', ...data });
        });

        channel.listen(
            '.ai.stream.pending-questions',
            (data: Omit<AiStreamPendingQuestionsEvent, 'type'>) => {
                sequencer.push({ type: 'ai.stream.pending-questions', ...data });
            },
        );

        channel.listen(
            '.ai.stream.thinking-delta',
            (data: Omit<AiStreamThinkingDeltaEvent, 'type'>) => {
                sequencer.push({ type: 'ai.stream.thinking-delta', ...data });
            },
        );

        channel.listen(
            '.ai.stream.summary-delta',
            (data: Omit<AiStreamSummaryDeltaEvent, 'type'>) => {
                sequencer.push({ type: 'ai.stream.summary-delta', ...data });
            },
        );

        channel.listen(
            '.ai.stream.follow-ups',
            (data: Omit<AiStreamFollowUpsEvent, 'type'>) => {
                sequencer.push({ type: 'ai.stream.follow-ups', ...data });
            },
        );

        channel.listen(
            '.ai.conversation.titled',
            (data: Omit<AiConversationTitledEvent, 'type'>) => {
                sequencer.push({ type: 'ai.conversation.titled', ...data });
            },
        );

        // Sandbox deliverable events (SD-7240) — async `build_document` results
        // that land minutes after the turn completes. Same channel; the
        // consumer keeps this subscription open until ready / failed arrives.
        channel.listen('.ai.sandbox.progress', (data: Omit<AiSandboxProgressEvent, 'type'>) => {
            sequencer.push({ type: 'ai.sandbox.progress', ...data });
        });

        channel.listen('.ai.sandbox.ready', (data: Omit<AiSandboxReadyEvent, 'type'>) => {
            sequencer.push({ type: 'ai.sandbox.ready', ...data });
        });

        channel.listen('.ai.sandbox.failed', (data: Omit<AiSandboxFailedEvent, 'type'>) => {
            sequencer.push({ type: 'ai.sandbox.failed', ...data });
        });

        // Echo private-channel auth failure (e.g. /broadcasting/auth 403) or a socket
        // drop. This is a TRANSPORT failure, not a turn failure — the box keeps working
        // and core keeps saving the turn regardless of whether this browser can hear it.
        // Synthesizing `ai.stream.failed` here used to force-fail every streaming bubble
        // and clear `isSending`, so a turn that finished 30s later was never shown until
        // reload (and a retry mid-turn hit `turn_in_flight` then paid for a second turn).
        // Emit a distinct event instead — the consumer keeps polling core for the saved
        // streamState and only finalizes once that poll (or the watchdog) says so.
        if (typeof channel.error === 'function') {
            channel.error((authError: unknown) => {
                subject.next({
                    type: 'ai.stream.channel-error',
                    conversationId,
                });
                // eslint-disable-next-line no-console
                console.error(
                    `GableStreamService: channel auth/connect failed (${channelName}):`,
                    authError,
                );
            });
        }

        let closed = false;
        const close = (): void => {
            if (closed) {
                return;
            }
            closed = true;
            sequencer.dispose();
            try {
                this._echo.leave(channelName);
            } catch (error) {
                console.error(`GableStreamService: error leaving channel ${channelName}:`, error);
            }
            subject.complete();
        };

        return {
            events$: subject.asObservable(),
            close,
        };
    }
}
